<script setup lang="ts">
import { Translation } from 'vue-i18n';
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type { AppButtonState } from '@libs/ui';
import { AppButton } from '@libs/ui';
import type { Money } from '@libs/trip-planner-interfaces';
import { formatWithCurrency } from '@libs/utils';

import type { SelectedActivityTicketsInfo } from '../models/activityDetailsSummary';

const { t } = i18n.global;

interface ActivityDetailsPriceSummaryDesktopProps {
  selectedTickets?: SelectedActivityTicketsInfo;
  fromCheapestPrice?: Money;
}

const props = defineProps<ActivityDetailsPriceSummaryDesktopProps>();

interface ActivityDetailsPriceSummaryDesktopEmits {
  (event: 'open-tickets-selector'): void;
}
const emit = defineEmits<ActivityDetailsPriceSummaryDesktopEmits>();

const areTicketsSelected = computed(
  () =>
    props.selectedTickets?.selectedTicketTypes.length &&
    props.selectedTickets.totalPrice
);

const formattedFromPrice = computed(() => {
  if (!props.fromCheapestPrice) {
    return;
  }
  return formatWithCurrency(
    props.fromCheapestPrice.amount,
    props.fromCheapestPrice.currency
  );
});

const formattedTotalPrice = computed(() => {
  if (!props.selectedTickets?.totalPrice) {
    return;
  }
  return formatWithCurrency(
    props.selectedTickets.totalPrice.amount,
    props.selectedTickets.totalPrice.currency
  );
});

const appButtonState = computed<AppButtonState>(() => {
  if (!props.fromCheapestPrice && !props.selectedTickets) {
    return 'loadingSpinner';
  }
  return !props.fromCheapestPrice ? 'disabled' : 'enabled';
});

const appButtonText = computed<string>(() =>
  !props.fromCheapestPrice
    ? t('planner.no_dates_available')
    : areTicketsSelected.value
    ? t('planner.change')
    : t('planner.show_dates')
);
</script>

<template>
  <div class="activity-details-price-summary-desktop">
    <div
      v-if="props.fromCheapestPrice || areTicketsSelected"
      class="tickets-price-wrapper"
    >
      <div v-if="props.fromCheapestPrice && !areTicketsSelected">
        <Translation
          keypath="planner.from_price_per_person"
          tag="span"
          class="from-price"
        >
          <template #price>
            <span class="price">{{ formattedFromPrice }}</span>
          </template>
        </Translation>
      </div>
      <div
        v-else-if="
          areTicketsSelected &&
          props.selectedTickets &&
          props.selectedTickets.selectedTicketTypes.length === 1 &&
          props.selectedTickets.selectedTicketTypes[0]
        "
        class="selected-tickets-1"
      >
        <span class="selected-tickets-amount">
          {{
            `${props.selectedTickets.selectedTicketTypes[0]?.amount} ${$t(
              'planner.tickets',
              props.selectedTickets.selectedTicketTypes[0]?.amount
            )}`
          }}
        </span>
        <span class="selected-tickets-info">
          {{
            `${props.selectedTickets.selectedTicketTypes[0].date}, ${props.selectedTickets.selectedTicketTypes[0].ticketRate}, ${props.selectedTickets.selectedTicketTypes[0].time}`
          }}
        </span>
        <span class="total-price">{{ formattedTotalPrice }}</span>
      </div>
      <div
        v-else-if="areTicketsSelected && props.selectedTickets"
        class="selected-tickets-2"
      >
        <div
          v-for="selectedTicket in props.selectedTickets.selectedTicketTypes"
          :key="selectedTicket.ticketRate"
          class="selected-tickets-info"
        >
          <span class="selected-tickets-amount">
            {{
              `${selectedTicket?.amount} ${$t(
                'planner.tickets',
                selectedTicket?.amount
              )} -`
            }}
          </span>
          <span>
            {{
              `${selectedTicket.date}, ${selectedTicket.ticketRate}, ${selectedTicket.time}`
            }}
          </span>
        </div>
        <span class="total-price">{{ formattedTotalPrice }}</span>
      </div>
    </div>
    <div class="button-wrapper">
      <AppButton
        type="important"
        :state="appButtonState"
        size="large"
        :wide="true"
        :ariaLabel="$t('a11y.open_tickets_selector')"
        @click="emit('open-tickets-selector')"
      >
        {{ appButtonText }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.activity-details-price-summary-desktop {
  border: 1px solid var(--color-neutral-20);
  border-radius: var(--border-radius-12);
  box-shadow: var(--shadow-elevation-200);

  .tickets-price-wrapper {
    border-bottom: 1px solid var(--color-neutral-20);
    padding: var(--spacing-24);
    align-items: center;
    text-align: center;

    .from-price {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-8);
      @include font-body-l-light;
      .price {
        @include font-heading-3;
      }
    }
    .selected-tickets-1 {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-4);
      .selected-tickets-amount {
        @include font-body-l-bold;
      }
      .selected-tickets-info {
        @include font-body-m-light;
      }
      .total-price {
        margin-top: var(--spacing-12);
        @include font-heading-3;
      }
    }
    .selected-tickets-2 {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-4);
      .selected-tickets-info {
        @include font-body-m-light;
        .selected-tickets-amount {
          @include font-body-m-bold;
        }
      }

      .total-price {
        margin-top: var(--spacing-12);
        @include font-heading-3;
      }
    }
  }
  .button-wrapper {
    padding: var(--spacing-24);
  }
}
</style>
